<template>
  <div class="home-view">
    <!-- Sección de partida existente -->
    <section v-if="partida && game" class="existing-session-section">
      <div class="session-card">
        <div class="session-header">
          <h3>{{ displaySessionPartidaTitle }}</h3>
        </div>
        
        <!-- Game Card - Same style as NuevaPartidaView -->
        <div class="game-card-container">
          <div 
            class="game-card"
            :style="{ '--game-bg-color': game.background_color || '#6c757d' }"
          >
            <div class="game-content">
              <div v-if="getGameLogo(game)" class="game-logo">
                <img 
                  :src="getFileUrl(getGameLogo(game))" 
                  :alt="game.name?.[language] || game.name?.es"
                  @error="handleImageError"
                />
              </div>
              <h4 class="game-name">  
                {{ game.name?.[language] || game.name?.es || 'Juego sin nombre' }}
              </h4>
            </div>
            <div 
              v-if="game.pattern"
              class="game-pattern-line"
              :style="{ backgroundImage: `url(${getFileUrl(game.pattern)})` }"
            >
              <div v-if="game.country" class="game-country">
                {{ game.country.toUpperCase() }}
              </div>
            </div>
          </div>
        </div>
        
        <div class="session-details">
          <div class="detail-item">
            <span class="label">{{ displaySessionPartidaCodeLabel }}</span>
            <span class="value">{{ partida.partidaCode }}</span>
          </div>
          <div class="detail-item">
            <span class="label">{{ displaySessionPartidaLastActivityLabel }}</span>
            <span class="value">{{ formatDate(partida.last_activity) }}</span>
          </div>
        </div>
        <div class="session-actions">
          <button 
            @click="continueSessionPartida()" 
            class="continue-session-btn primary-btn"
          >
            {{ displaySessionPartidaContinueButton }}
          </button>
        </div>
      </div>
    </section>

    <div class="actions-grid">
      <router-link to="/nueva-partida" class="action-card primary">
        <div class="card-icon">🚀</div>
        <h3>{{ displayStartGameTitle }}</h3>
        <p>{{ displayStartGameDescription }}</p>
      </router-link>

      <router-link to="/continue-partida" class="action-card secondary">
        <div class="card-icon">▶️</div>
        <h3>{{ displayContinueOtherGameTitle }}</h3>
        <p>{{ displayContinueOtherGameDescription }}</p>
      </router-link>

      <router-link to="/about" class="action-card about-button tertiary">
        <div class="card-icon">ℹ️</div>
        <h3>{{ displayAboutTitle }}</h3>
        <p>{{ displayAboutDescription }}</p>
      </router-link>
    </div>
  </div>
</template>

<script>
import { useConfig } from '@/composables/useConfig'
import { usePartida, useGame } from '../composables/usePartidaContext'
import { useLanguage } from '../composables/useLanguage'
import fileService from '&/services/fileService'

export default {
  name: 'HomeView',
  setup() {
    const { getConfigText } = useConfig()
    const { partida, loadSessionPartida } = usePartida();
    const { game } = useGame();
    const { language } = useLanguage();

    return {
      getConfigText,
      partida,
      loadSessionPartida,
      game,
      language
    }
  },
  computed: {
    // Textos para la sección de partida existente
    displaySessionPartidaTitle() {
      return this.getConfigText('sessionPartida.title', '🎮 Tienes una partida en progreso')
    },
    displaySessionPartidaCodeLabel() {
      return this.getConfigText('sessionPartida.codeLabel', 'Código:')
    },
    displaySessionPartidaGameNombre() {
      return this.getConfigText('sessionPartida.gameNombre', 'Nombre del Juego:')
    },
    displaySessionPartidaLastActivityLabel() {
      return this.getConfigText('sessionPartida.lastActivityLabel', 'Última actividad:')
    },
    displaySessionPartidaContinueButton() {
      return this.getConfigText('sessionPartida.continueButton', '🎯 Continuar partida actual')
    },
    // Textos existentes para los botones de acción
    displayStartGameTitle() {
      return this.getConfigText('nuevaPartida.title', 'Nueva Partida')
    },
    displayStartGameDescription() {
      return this.getConfigText('nuevaPartida.description', 'Configura y comienza a jugar')
    },
    displayContinueOtherGameTitle() {
      return this.getConfigText('nuevaPartida.continueButtonTitle', 'Continuar otra partida')
    },
    displayContinueOtherGameDescription() {
      return this.getConfigText('nuevaPartida.continueButtonDescription', 'Introduce código de partida guardada')
    },
    displayAboutTitle() {
      return this.getConfigText('about.title', 'Acerca De')
    },
    displayAboutDescription() {
      return this.getConfigText('about.description', 'Información sobre el proyecto')
    }
  },
  created() {
    this.loadSessionPartida()
  },
  methods: {
    async continueSessionPartida() {
      if (!this.partida) return
      
      try {
        console.log('🎮 [HomeView] Continuando partida de sesión:', this.partida.partidaCode)
        this.navigateToPartida(this.partida.partidaCode)
      } catch (error) {
        console.error('❌ [HomeView] Error continuando partida de sesión:', error)
      }
    },

    navigateToPartida(partidaCode) {
      this.$router.push(`/partida/${partidaCode}`)
    },

    formatDate(dateString) {
      if (!dateString) return this.getConfigText('continuePartida.noDateText', 'Sin fecha')
      
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) {
          return this.getConfigText('continuePartida.invalidDateText', 'Fecha inválida')
        }
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
      } catch (error) {
        return this.getConfigText('continuePartida.invalidDateText', 'Fecha inválida')
      }
    },

    getFileUrl(filename) {
      return fileService.getRelativeFileUrl(filename)
    },

    getGameLogo(game) {
      if (!game.logo) return null
      
      // Si logo es un string simple, devolverlo directamente
      if (typeof game.logo === 'string') {
        return game.logo
      }
      
      // Si logo es un objeto multiidioma, buscar por idioma actual
      if (typeof game.logo === 'object' && game.logo !== null) {
        // Primero intentar idioma actual
        if (game.logo[this.language]) {
          return game.logo[this.language]
        }
        // Luego intentar español como fallback
        if (game.logo.es) {
          return game.logo.es
        }
        // Finalmente, devolver el primer logo disponible
        const availableLogos = Object.values(game.logo).filter(logo => logo && logo.trim())
        return availableLogos.length > 0 ? availableLogos[0] : null
      }
      
      return null
    },

    handleImageError(event) {
      console.warn('⚠️ Error loading image:', event.target.src)
      event.target.style.display = 'none'
    },
  }
}
</script>

<style scoped>
.home-view {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

/* Nueva sección de partida existente */
.existing-session-section {
  margin-bottom: var(--section-padding);
}

.session-card {
  background: var(--app-gradient);
  color: var(--text-light);
  padding: var(--section-padding);
  border-radius: 15px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.session-header h3 {
  margin: 0 0 var(--spacing-2xl) 0;
  font-size: var(--heading-font-size-h2);
  text-align: center;
}

/* Game Card Styles - Same as NuevaPartidaView */
.game-card-container {
  margin: var(--spacing-xl) 0;
}

.game-card {
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  min-height: 200px;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.game-pattern-line {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  background-size: cover;
  background-position: center;
  background-repeat: repeat-x;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
}

.game-country {
  color: white;
  font-weight: bold;
  font-size: var(--form-font-size);
  letter-spacing: 1px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  background: rgba(0, 0, 0, 0.3);
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  backdrop-filter: blur(5px);
}

.game-content {
  position: relative;
  z-index: 2;
  padding: var(--section-padding);
  padding-top: 0rem;
  text-align: center;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: var(--game-bg-color, #6c757d);
}

.game-logo {
  margin-bottom: 0rem;
}

.game-logo img {
  max-height: 100px;
  width: auto;
  filter: brightness(1.1) contrast(1.1);
}

.game-name {
  font-size: var(--heading-font-size-h4);
  font-weight: bold;
  margin-bottom: var(--card-margin);
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.session-details {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-2xl);
}

.detail-item {
  text-align: center;
  min-width: 120px;
}

.detail-item .label {
  display: block;
  font-size: var(--form-font-size);
  opacity: 0.8;
  margin-bottom: var(--spacing-xs);
}

.detail-item .value {
  display: block;
  font-weight: bold;
  font-size: var(--heading-font-size-h4);
}

.session-actions {
  text-align: center;
}

.continue-session-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.5);
  padding: var(--button-padding-y) var(--spacing-2xl);
  border-radius: 8px;
  font-size: var(--button-font-size);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.continue-session-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.8);
  transform: translateY(-2px);
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--section-padding);
  margin-bottom: var(--section-padding);
}

.action-card {
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  padding: var(--section-padding);
  border-radius: 15px;
  text-decoration: none;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.action-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 45px rgba(0, 0, 0, 0.2);
  text-decoration: none;
}

.action-card.primary {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.action-card.secondary {
  background: linear-gradient(135deg, #4ab172 0%, #b7ec5b 100%);
  color: white;
}

.action-card.tertiary {
  background: linear-gradient(135deg, #fc71d4 0%, #84a8e7 100%);
  color: white;
}

.action-card.primary:hover {
  color: white;
}

.card-icon {
  font-size: var(--font-size-3xl);
  margin-bottom: var(--card-margin);
}

.action-card h3 {
  font-size: var(--heading-font-size-h2);
  margin-bottom: var(--spacing-sm);
}

.action-card p {
  font-size: var(--body-font-size);
  opacity: 0.8;
  line-height: 1.4;
  margin: 0;
}

/* Responsive design */
@media (max-width: 768px) {
  .actions-grid {
    grid-template-columns: 1fr;
    gap: var(--section-padding);
  }
  
  .action-card {
    padding: var(--spacing-2xl);
  }
  
  .card-icon {
    font-size: var(--heading-font-size-h1);
  }

  .session-card {
    padding: var(--spacing-2xl);
  }
  
  .session-details {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .detail-item {
    min-width: auto;
  }
  
  /* Responsive styles for game card */
  .game-content { 
    padding: var(--spacing-2xl);
    min-height: 150px;
  }
}

@media (max-width: 480px) {
  .action-card {
    padding: var(--spacing-xl);
  }
  
  .action-card h3 {
    font-size: var(--heading-font-size-h3);
  }
  
  .action-card p {
    font-size: var(--form-font-size);
  }

  .session-card {
    padding: var(--spacing-xl);
  }
  
  .session-header h3 {
    font-size: var(--heading-font-size-h3);
  }
  
  .continue-session-btn {
    padding: var(--spacing-md) var(--spacing-xl);
    font-size: var(--form-font-size);
  }
  
  .game-name {
    font-size: var(--heading-font-size-h4);
  }
}

/* Estilos específicos para pantallas muy pequeñas (360px) */
@media (max-width: 360px) {
  .home-container {
    padding: var(--spacing-md);
  }
  
  .actions-grid {
    grid-template-columns: 1fr;
    gap: var(--section-padding);
  }

  .action-card {
    padding: var(--card-padding);
  }
  
  .action-card h3 {
    font-size: var(--body-font-size-lg);
    margin-bottom: var(--spacing-sm);
  }
  
  .action-card p {
    font-size: var(--body-font-size-sm);
    line-height: 1.4;
  }

  .session-card {
    padding: var(--card-padding);
    margin-bottom: 0.75rem;
  }
  
  .session-header h3 {
    font-size: var(--body-font-size-lg);
    margin-bottom: 0.75rem;
  }
  
  .session-details {
    gap: 0.5rem;
    margin-bottom: var(--card-margin);
  }

  .detail-item {
    min-width: 100px;
  }

  .detail-item .label {
    font-size: var(--body-font-size-xs);
  }

  .detail-item .value {
    font-size: var(--font-size-base);
  }
  
  .continue-session-btn {
    padding: var(--button-padding-y) 1rem;
    font-size: var(--body-font-size-sm);
  }
}
</style>
